import { Link } from "react-router";

import { Notice, Page } from "@/components/page";
import { subText, table, td, th } from "@/components/table";
import { useAdminStatus, useMe } from "@/lib/api";
import { maySeeOperations } from "@/lib/capability";
import { errorMessage, inZone } from "@/lib/format";

/**
 * Operations: is HROps working, and what is quietly doing nothing.
 *
 * This is the whole of what Admin adds. Every functional capability — raising a
 * leaver, reverting one, putting somebody back, flagging a risk — belongs to
 * HR, because that is the job; splitting it across roles only means HR waiting
 * on IT. What HR should never have to ask is whether the machinery behind the
 * screens is running, and that question has an audience of about two people.
 *
 * Ordered the way somebody asks when a leaver calls the helpdesk still able to
 * log in: is anything stuck, is the worker on, which leg last worked, and what
 * is not configured. The "not configured" section is the one that earns the
 * page — a half-finished rollout looks exactly like a working system until
 * something states the consequence out loud.
 */
export function AdminPage() {
  const me = useMe().data;
  const allowed = maySeeOperations(me);
  const { data, isLoading, error } = useAdminStatus(allowed);

  if (!allowed) {
    return (
      <Page title="Operations">
        <Notice>
          This is the operations view and needs the Admin role. Everything HROps actually does is on
          the other screens —{" "}
          <Link to="/queue" className="underline">
            the queue
          </Link>{" "}
          is the working one.
        </Notice>
      </Page>
    );
  }

  return (
    <Page
      title="Operations"
      intro="Whether the machinery behind the screens is running, read from the action log rather than from a synthetic probe. Nothing here changes anything."
    >
      {isLoading ? (
        <Notice>Reading the state…</Notice>
      ) : error ? (
        <Notice tone="danger">{errorMessage(error, "Could not read the status.")}</Notice>
      ) : !data ? null : (
        <div className="max-w-3xl">
          {/* First, because it is the only section that means somebody has to
              do something today. */}
          <section className="mt-6">
            <h2 className="text-fg text-sm font-semibold">
              Needs attention
              <span className="text-fg-muted ml-2 font-normal tabular-nums">
                {data.stuck?.length ?? 0}
              </span>
            </h2>
            {(data.stuck?.length ?? 0) === 0 ? (
              <p className={`${subText} mt-1`}>
                Nothing is half-applied, failed or waiting to be re-enabled.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className={table}>
                  <thead>
                    <tr>
                      <th scope="col" className={th}>
                        Subject
                      </th>
                      <th scope="col" className={th}>
                        Status
                      </th>
                      <th scope="col" className={th}>
                        Waiting since
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.stuck?.map((row) => (
                      <tr key={row.id}>
                        <td className={td}>
                          <Link to={`/leaver/${row.id}`} className="text-fg underline">
                            {row.subject_upn}
                          </Link>
                        </td>
                        <td className={`${td} text-fg`}>{row.status}</td>
                        <td className={`${td} text-fg-muted tabular-nums`}>
                          {inZone(row.since, null).time}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mt-8">
            <h2 className="text-fg text-sm font-semibold">Configuration</h2>
            <p className={`${subText} mt-1`}>
              What is switched on, and what an unset value silently means.
            </p>
            <dl className="mt-3 space-y-2">
              {data.configuration?.map((item) => (
                <div key={item.name} className="flex gap-3">
                  <dt className="text-fg w-52 shrink-0 text-sm">
                    {item.name}
                    <span className={`${subText} ml-2`}>{item.configured ? "on" : "off"}</span>
                  </dt>
                  <dd className={`${subText} flex-1`}>{item.detail}</dd>
                </div>
              ))}
            </dl>
          </section>

          <section className="mt-8">
            <h2 className="text-fg text-sm font-semibold">Tenant legs</h2>
            <p className={`${subText} mt-1`}>
              When each last worked and when it last did not. Read from what happened to real
              people, not from a test call.
            </p>
            {(data.mechanisms?.length ?? 0) === 0 ? (
              <p className={`${subText} mt-2`}>
                Nothing has been attempted yet, so there is nothing to report.
              </p>
            ) : (
              <div className="mt-3 overflow-x-auto">
                <table className={table}>
                  <thead>
                    <tr>
                      <th scope="col" className={th}>
                        Leg
                      </th>
                      <th scope="col" className={th}>
                        Last success
                      </th>
                      <th scope="col" className={th}>
                        Last failure
                      </th>
                      <th scope="col" className={th}>
                        Failures (7d)
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mechanisms?.map((m) => (
                      <tr key={m.mechanism}>
                        <td className={`${td} text-fg`}>{m.mechanism}</td>
                        <td className={`${td} text-fg-muted tabular-nums`}>
                          {m.last_success ? inZone(m.last_success, null).time : "never"}
                        </td>
                        <td className={`${td} text-fg-muted tabular-nums`}>
                          {m.last_failure ? inZone(m.last_failure, null).time : "never"}
                        </td>
                        <td className={`${td} tabular-nums`}>
                          <span className={m.failures_last_7_days ? "text-warning-fg" : "text-fg"}>
                            {m.failures_last_7_days ?? 0}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="mt-8">
            <h2 className="text-fg text-sm font-semibold">Recent worker passes</h2>
            {(data.recent_passes?.length ?? 0) === 0 ? (
              <p className={`${subText} mt-1`}>
                The worker has never completed a pass against this database.
              </p>
            ) : (
              <ul className={`${subText} mt-2 space-y-1`}>
                {data.recent_passes?.map((p, index) => (
                  <li key={`${p.at}-${index}`}>
                    <span className="tabular-nums">{inZone(p.at, null).time}</span> {p.action}{" "}
                    <span className={p.outcome === "succeeded" ? "" : "text-warning-fg"}>
                      {p.outcome}
                    </span>
                    {p.subject_upn ? ` · ${p.subject_upn}` : ""}
                    {p.error ? <span className="text-danger-fg block">{p.error}</span> : null}
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="mt-8 mb-4">
            <h2 className="text-fg text-sm font-semibold">Totals</h2>
            <p className={`${subText} mt-1`}>
              {Object.entries(data.offboardings_by_status ?? {})
                .map(([status, count]) => `${count} ${status}`)
                .join(" · ") || "No records yet"}
              {" · "}
              {data.active_risk_flags ?? 0} active risk flag
              {(data.active_risk_flags ?? 0) === 1 ? "" : "s"}
            </p>
          </section>
        </div>
      )}
    </Page>
  );
}
